// src/pages/admin/VehiclesAdmin.jsx
import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getVehicles,
  addCar,
  updateCar,
  deleteCar,
  bulkUpdateCars,
  bulkDeleteCars,
  bulkEnrichCars,
  bulkAddBasicCars,
  getBulkUploadInfo,
} from "../../api/cars";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowPathIcon,
  MagnifyingGlassCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import VehicleForm from "../../components/admin/VehicleForm";
import ConfirmModal from "../../components/ConfirmModal";
import { FaUpload } from "react-icons/fa6";
import LoadingSpinner from "../../components/LoadingSpinner";
import { vehicleSchema } from "../../validation/vehicle.schema";
import useDebounce from "../../stores/useDebounce";
import BulkUploadStats from "../../components/admin/BulkUploadStats";
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

// row-status styles
const statusStyles = {
  draft: "bg-gray-200 text-gray-800",
  available: "bg-success/20 text-success",
  reserved: "bg-warning/20 text-warning",
  sold: "bg-danger/20 text-danger",
  "take-off": "bg-purple-200 text-purple-800",
};
const STATUSES = ["draft", "available", "reserved", "sold", "take-off"];

export default function VehiclesAdmin() {
  const qc = useQueryClient();
  const selectAllRef = useRef();
  const formRef = useRef(null);

  // ─── UI state ────────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const [selected, setSelected] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState(STATUSES[0]);

  // ─── Fetch with filters ──────────────────────────────────────────────────
  const { data: resp = {}, isLoading } = useQuery({
    queryKey: [
      "adminCars",
      { search: debouncedSearch, statusFilter, page, limit, sortBy, sortOrder },
    ],
    queryFn: () =>
      getVehicles({
        q: debouncedSearch.trim() || undefined,
        status: statusFilter || undefined,
        page,
        limit,
        sortBy,
        sortOrder,
      }),
    keepPreviousData: true,
  });

  // now each item is { base: {...}, extended: {...}, mot:[], rateExVAT, vatCharge }
  const vehicles = resp.data || [];
  const totalPages = resp.totalPages || 1;

  // ─── File upload / bulk mutations unchanged ──────────────────────────────
  const [file, setFile] = useState(null);
  const onFileChange = (e) => setFile(e.target.files[0]);

  // ─── Checkbox indeterminate ──────────────────────────────────────────────
  useEffect(() => {
    if (!selectAllRef.current) return;
    const all = vehicles.length;
    const sel = selected.length;
    selectAllRef.current.indeterminate = sel > 0 && sel < all;
  }, [selected, vehicles]);

  useEffect(() => {
    if (formOpen && formRef.current) {
      formRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [formOpen]);

  // ─── CRUD Mutations ──────────────────────────────────────────────────────
  const addMutation = useMutation({
    mutationFn: addCar,
    onSuccess: (newCar) => {
      qc.invalidateQueries(["adminCars"]);
      toast.success(`✅ Vehicle ${newCar.registration} added`);
      setFormOpen(false);
    },
    onError: (e) => toast.error(e.response?.data?.error || "❌ Add failed"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => updateCar(id, data),
    onSuccess: (u) => {
      qc.invalidateQueries(["adminCars"]);
      toast.success(`✏️ Vehicle ${u.registration} updated`);
      setFormOpen(false);
      setEditing(null);
    },
    onError: (e) => toast.error(e.response?.data?.error || "❌ Update failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteCar(id),
    onSuccess: () => {
      qc.invalidateQueries(["adminCars"]);
      toast.success("🗑️ Vehicle deleted");
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(e.response?.data?.error || "❌ Delete failed"),
  });

  const bulkAddBasicMutation = useMutation({
    mutationFn: (fileData) => {
      const form = new FormData();
      form.append("file", fileData);
      return bulkAddBasicCars(form);
    },
    onSuccess: ({ totalRows, upserted }) => {
      toast.success(`📥 Imported ${upserted}/${totalRows} rows`);
      qc.invalidateQueries(["adminCars"]);
      setFile(null);
    },
    onError: (e) =>
      toast.error(e.response?.data?.message || "❌ Bulk add failed"),
  });

  const bulkEnrichMutation = useMutation({
    mutationFn: () => bulkEnrichCars(),
    onSuccess: ({ attempted }) => {
      toast.success(`🔄 Enriched ${attempted} vehicles`);
      qc.invalidateQueries(["adminCars"]);
    },
    onError: (e) =>
      toast.error(e.response?.data?.message || "❌ Enrich failed"),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: ({ ids, status }) => bulkUpdateCars({ ids, status }),
    onSuccess: ({ modifiedCount, status }) => {
      qc.invalidateQueries(["adminCars"]);
      toast.success(`✔️ ${modifiedCount} → ${status}`);
      setSelected([]);
    },
    onError: (e) =>
      toast.error(e.response?.data?.message || "❌ Bulk update failed"),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => bulkDeleteCars(ids),
    onSuccess: ({ deletedCount }) => {
      qc.invalidateQueries(["adminCars"]);
      toast.success(`🗑️ Deleted ${deletedCount} vehicle(s)`);
      setSelected([]);
      setBulkOpen(false);
    },
    onError: (e) =>
      toast.error(e.response?.data?.message || "❌ Bulk delete failed"),
  });

  const { data: bulkInfo } = useQuery({
    queryKey: ["bulkUploadInfo"],
    queryFn: getBulkUploadInfo,
    staleTime: 5 * 60 * 1000,
  });

  // ─── Handlers ────────────────────────────────────────────────────────────
  const onSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };
  const onStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };
  const requestSort = (key) => {
    if (sortBy === key) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
  };
  const confirmDelete = () => {
    deleteTarget
      ? deleteMutation.mutate(deleteTarget)
      : bulkDeleteMutation.mutate(selected);
  };

  return (
    <div className="p-6">
      {/* Toolbar */}
      <h2 className="text-2xl font-bold mb-5">Manage Vehicles</h2>
      <div className="flex flex-wrap items-center justify-between mb-4">
        {/* Search + Status */}
        <div className="flex gap-2 w-[30%]">
          <input
            type="text"
            placeholder="Search registration or VIN…"
            value={search}
            onChange={onSearchChange}
            className="w-full border rounded px-3 py-1"
          />
          <select
            value={statusFilter}
            onChange={onStatusChange}
            className="border rounded text-primary py-1"
          >
            <option value="">All status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Add / Import / Enrich */}
        <div className="flex gap-2 w-[65%] justify-end">
          <button
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            className="flex items-center bg-surface px-4 py-2 rounded hover:bg-accent"
          >
            <PlusIcon className="h-5 w-5 mr-2" /> Add Vehicle
          </button>
          <label className="flex items-center bg-surface px-4 py-2 rounded hover:bg-accent">
            <FaUpload className="h-5 w-5 mr-2" />
            <span>{file ? file.name : "Upload CSV"}</span>
            <input
              type="file"
              accept=".csv"
              onChange={onFileChange}
              className="hidden"
            />
          </label>
          <button
            disabled={!file || bulkAddBasicMutation.isLoading}
            onClick={() => bulkAddBasicMutation.mutate(file)}
            className="bg-surface px-4 py-2 rounded hover:bg-accent"
          >
            {bulkAddBasicMutation.isLoading ? "Importing…" : "Import CSV"}
          </button>
          <button
            onClick={() => bulkEnrichMutation.mutate()}
            disabled={bulkEnrichMutation.isLoading}
            className="flex items-center bg-surface px-4 py-2 rounded hover:bg-accent"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            {bulkEnrichMutation.isLoading ? "Enriching…" : "Enrich Data"}
          </button>
        </div>
      </div>

      <BulkUploadStats
        bulkInfo={bulkInfo}
        limit={limit}
        setLimit={setLimit}
        setPage={setPage}
      />

      {/* Form */}
      {formOpen && (
        <div className="mb-6" ref={formRef}>
          <VehicleForm
            key={editing?.id || "new"}
            initialData={editing || {}}
            isEdit={!!editing}
            onSubmit={(data) => {
              if (editing) updateMutation.mutate({ id: editing.id, ...data });
              else addMutation.mutate(data);
            }}
            onCancel={() => {
              setFormOpen(false);
              setEditing(null);
            }}
            formSchema={vehicleSchema}
          />
        </div>
      )}

      {/* Bulk Actions */}
      {selected.length > 1 && (
        <div className="flex items-center gap-2 mb-4">
          Bulk Actions:
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            className="border text-primary rounded py-1"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
          <button
            onClick={() =>
              bulkStatusMutation.mutate({ ids: selected, status: bulkStatus })
            }
            className="px-3 py-1 bg-surface rounded hover:bg-accent"
          >
            Set status to {bulkStatus}
          </button>
          <button
            onClick={() => setBulkOpen(true)}
            className="px-3 py-1 bg-accent rounded text-white hover:bg-danger"
          >
            Delete selected
          </button>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="overflow-x-auto bg-surface rounded-lg shadow py-5 mb-6">
            {vehicles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <MagnifyingGlassIcon className="h-12 w-12 text-white mb-4" />
                <h3 className="text-lg font-semibold text-accent">
                  No vehicles found
                </h3>
                <p className="mt-2 text-sm text-white/80 text-center max-w-xs">
                  Try adjusting your search criteria or filters, or add a new
                  vehicle.
                </p>
                <div className="mt-4 flex space-x-2">
                  <button
                    onClick={() => {
                      setSearch("");
                      setStatusFilter("");
                      setPage(1);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
                  >
                    Clear filters
                  </button>
                  <button
                    onClick={() => {
                      setEditing(null);
                      setFormOpen(true);
                    }}
                    className="px-4 py-2 bg-accent text-white rounded hover:opacity-90 transition"
                  >
                    Add vehicle
                  </button>
                </div>
              </div>
            ) : (
              <table className="w-full table-auto">
                <thead className="sticky top-0 bg-surface">
                  <tr>
                    <th className="p-2 text-center">
                      <input
                        type="checkbox"
                        ref={selectAllRef}
                        checked={
                          selected.length === vehicles.length &&
                          vehicles.length > 0
                        }
                        onChange={(e) =>
                          setSelected(
                            e.target.checked
                              ? vehicles.map((v) => v.base.id)
                              : []
                          )
                        }
                      />
                    </th>
                    {[
                      { label: "Registration", key: "registration" },
                      { label: "Make", key: "make" },
                      { label: "Model", key: "model" },
                      { label: "Year", key: "year" },
                      { label: "Price", key: "price" },
                      { label: "Status", key: "status" },
                      { label: "Enrich", key: "enrichment" },
                    ].map((col) => (
                      <th
                        key={col.key}
                        className="p-2 text-left cursor-pointer"
                        onClick={() => requestSort(col.key)}
                      >
                        {col.label}{" "}
                        <SortIcon
                          columnKey={col.key}
                          sortBy={sortBy}
                          sortOrder={sortOrder}
                        />
                      </th>
                    ))}
                    <th className="p-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v) => {
                    console.log("full vehicle data", v);
                    const { base, extended } = v;
                    const id = base.id;
                    // ← changed: id now from base.id
                    const registration = base.registration;
                    const makeText = extended.make;
                    const modelText = extended.model;
                    const yearText = extended.year;
                    const price = base.price;
                    const statusText = base.status;

                    return (
                      <tr
                        key={id}
                        className="odd:bg-background even:bg-lightsec"
                      >
                        <td className="p-2 text-center">
                          <input
                            type="checkbox"
                            checked={selected.includes(id)}
                            onChange={() =>
                              setSelected((s) =>
                                s.includes(id)
                                  ? s.filter((x) => x !== id)
                                  : [...s, id]
                              )
                            }
                          />
                        </td>

                        <td className="p-2">{registration}</td>
                        <td className="p-2">{makeText}</td>
                        <td className="p-2">{modelText}</td>
                        <td className="p-2">{yearText || "—"}</td>
                        <td className="p-2">
                          {price != null ? `£${price?.toLocaleString()}` : "0"}
                        </td>
                        <td className="p-2">
                          <span
                            className={`px-2 py-1 rounded text-sm ${statusStyles[statusText]}`}
                          >
                            {statusText.charAt(0).toUpperCase() +
                              statusText.slice(1)}
                          </span>
                        </td>

                        <td className="p-2 text-center">
                          {v.enrichment?.status === "success" ? (
                            <CheckCircleIcon
                              className="inline h-6 w-6 text-success"
                              title="Enrichment succeeded"
                            />
                          ) : v.enrichment?.status === "failed" ? (
                            <XCircleIcon
                              className="inline h-6 w-6 text-danger"
                              title={
                                v.enrichment.error ??
                                "Error details unavailable"
                              }
                            />
                          ) : (
                            <ClockIcon
                              className="inline h-6 w-6 text-warning"
                              title="Enrichment pending"
                            />
                          )}
                        </td>

                        {/* <td className="p-2 text-center">
                          {v.enrichment?.status === "success" ? (
                            <span className="inline-block px-2 py-1 rounded text-sm bg-success/20 text-success">
                              ✓ Success
                            </span>
                          ) : v.enrichment?.status === "failed" ? (
                            <span
                              className="inline-block px-2 py-1 rounded text-sm bg-danger/20 text-danger"
                              title={
                                v.enrichment.error ??
                                "Error details unavailable"
                              }
                            >
                              ⚠ Failed
                            </span>
                          ) : (
                            <span className="inline-block px-2 py-1 rounded text-sm bg-warning/20 text-yellow-200">
                              Pending
                            </span>
                          )}
                        </td> */}

                        <td className="p-2 flex justify-center gap-2">
                          <button
                            onClick={() => {
                              // ← changed: pull id + both nested objects into editing
                              setEditing({ id, ...base, ...extended });
                              setFormOpen(true);
                            }}
                            className="p-1 rounded hover:bg-lightsec"
                          >
                            <PencilIcon className="h-5 w-5 text-accent" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(id)}
                            className="p-1 rounded hover:bg-lightsec"
                          >
                            <TrashIcon className="h-5 w-5 text-danger" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          <div className="flex justify-between text-secondary-accent">
            <div>
              Page {resp.page} of {totalPages}
            </div>
            <div className="space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={resp.page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={resp.page === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Confirm delete */}
      <ConfirmModal
        isOpen={!!deleteTarget || bulkOpen}
        title="Confirm Delete"
        message={
          deleteTarget
            ? "Delete this vehicle?"
            : `Delete ${selected.length} vehicles?`
        }
        onCancel={() => {
          setDeleteTarget(null);
          setBulkOpen(false);
        }}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

// SortIcon helper unchanged
function SortIcon({ columnKey, sortBy, sortOrder }) {
  if (sortBy !== columnKey) {
    return <ChevronUpDownIcon className="inline h-4 w-4" />;
  }
  return sortOrder === "asc" ? (
    <ChevronUpIcon className="inline h-4 w-4" />
  ) : (
    <ChevronDownIcon className="inline h-4 w-4" />
  );
}
